import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { startOfDay, subDays } from "date-fns";

export interface DateFilterState {
  mode: "single" | "range";
  selectedDate: Date;
  dateRange: { start: Date; end: Date } | null;
}

const today = new Date();

const initialState: DateFilterState = {
  mode: "single",
  selectedDate: startOfDay(today),
  dateRange: null,
};

const dateFilterSlice = createSlice({
  name: "dateFilter",
  initialState,
  reducers: {
    setMode: (state, action: PayloadAction<"single" | "range">) => {
      state.mode = action.payload;
      // When switching to single mode, clear the date range
      if (action.payload === "single") {
        state.dateRange = null;
      }
    },
    setSingleDate: (state, action: PayloadAction<Date>) => {
      state.selectedDate = startOfDay(action.payload);
      state.mode = "single";
      state.dateRange = null;
    },
    setDateRange: (
      state,
      action: PayloadAction<{ start: Date; end: Date }>
    ) => {
      state.dateRange = {
        start: startOfDay(action.payload.start),
        end: startOfDay(action.payload.end),
      };
      state.mode = "range";
    },
    // Quick filter actions
    setToday: (state) => {
      state.selectedDate = startOfDay(new Date());
      state.mode = "single";
      state.dateRange = null;
    },
    setYesterday: (state) => {
      state.selectedDate = startOfDay(subDays(new Date(), 1));
      state.mode = "single";
      state.dateRange = null;
    },
    setLast7Days: (state) => {
      const today = new Date();
      const lastWeek = subDays(today, 7);
      state.dateRange = {
        start: startOfDay(lastWeek),
        end: startOfDay(today),
      };
      state.mode = "range";
    },
  },
});

export const {
  setMode,
  setSingleDate,
  setDateRange,
  setToday,
  setYesterday,
  setLast7Days,
} = dateFilterSlice.actions;

/**
 * Memoized selectors for date filter state
 */

// Base selector
const selectDateFilterState = (state: { dateFilter: DateFilterState }) =>
  state.dateFilter;

// Mode selector
export const selectDateMode = createSelector(
  [selectDateFilterState],
  (dateFilter) => dateFilter.mode
);

// Selected date selector
export const selectSelectedDate = createSelector(
  [selectDateFilterState],
  (dateFilter) => dateFilter.selectedDate
);

// Date range selector
export const selectDateRange = createSelector(
  [selectDateFilterState],
  (dateFilter) => dateFilter.dateRange
);

// Check if in single mode
export const selectIsSingleMode = createSelector(
  [selectDateMode],
  (mode) => mode === "single"
);

// Check if in range mode
export const selectIsRangeMode = createSelector(
  [selectDateMode],
  (mode) => mode === "range"
);

// Get active date configuration (for API calls)
export const selectActiveDateConfig = createSelector(
  [selectDateFilterState],
  (dateFilter) => {
    if (dateFilter.mode === "single") {
      return {
        mode: "single" as const,
        date: dateFilter.selectedDate,
      };
    } else {
      return {
        mode: "range" as const,
        startDate: dateFilter.dateRange?.start || new Date(),
        endDate: dateFilter.dateRange?.end || new Date(),
      };
    }
  }
);

// Composite selector for all date filter state
export const selectCompleteDateFilter = createSelector(
  [selectDateFilterState],
  (dateFilter) => ({
    mode: dateFilter.mode,
    selectedDate: dateFilter.selectedDate,
    dateRange: dateFilter.dateRange,
  })
);

export default dateFilterSlice.reducer;
